import { Enemy, LevelSim, SimEvent, SimEventKind } from "@highmarch/domain";
import type { EnemyView } from "./enemyView";

/**
 * The bosses' own clips (bosses v2, 2026-10-21; art/unitkit/README.md "Bosses v2", anim_bosses_v2.py). Every boss_*
 * model carries `roar` (its arrival: it climbs out of the ground roaring, played as bossStage rises it) and the ability
 * clips the sim announces: the Harpy Queen's `boost` (EnemyBoosted) and the Necromancer's `summon` (a minion rises at
 * its feet: an EnemySpawned standing exactly where it stands). Its `attack` and `hit` play `solo`: the boss clips key
 * every part, so averaged with the walk or idle under them they played at half strength. A model without a clip is
 * left alone (the clip calls return 0), and a rare or the Wyrm (their own ids) never comes here.
 */
export const BOSS_CLIPS = {
  arrival: "roar",
  ability: { boss_harpy_queen: "boost", boss_necromancer: "summon" } as Readonly<Record<string, string>>,
  /** The one-shots that cut the loop under them instead of averaging with it. */
  solo: new Set(["attack", "hit"]),
} as const;

/** A boss_* view (a boss's own model), not a rare or a level's boss-flagged grunt. */
export function isBossModel(v: EnemyView): boolean { return v.boss && v.archId.startsWith("boss_"); }

/** The boss appears (EnemyViews right after BossStage.spawn): it roars as it climbs out. */
export function bossArrive(v: EnemyView): void {
  if (isBossModel(v)) v.rig.playOnce(BOSS_CLIPS.arrival, false, null, { solo: true });
}

/** `attack` / `hit` on any enemy view: solo for a boss model, as before for everything else. Returns the clip length. */
export function playStrike(v: EnemyView, name: "attack" | "hit"): number {
  return v.rig.playOnce(name, false, null, { solo: isBossModel(v) && BOSS_CLIPS.solo.has(name) });
}

/**
 * The ability beats of this tick's events: EnemyBoosted on a Harpy Queen, a minion rising where a Necromancer stands.
 * `active` is EnemyViews' id → view map; nothing plays over a clip already running (an attack, the arrival).
 */
export function bossAbility(ev: SimEvent, active: ReadonlyMap<number, EnemyView>, sim: LevelSim): void {
  if (ev.kind === SimEventKind.EnemyBoosted) {
    const v = active.get(ev.a);
    if (v) ability(v);
    return;
  }
  if (ev.kind !== SimEventKind.EnemySpawned) return;
  const minion = sim.findEnemy(ev.a);
  if (minion === null) return;
  for (const [id, v] of active) {
    if (id === ev.a || v.archId !== "boss_necromancer") continue;
    const boss: Enemy | null = sim.findEnemy(id);
    if (boss !== null && boss.x.eq(minion.x) && boss.y.eq(minion.y)) ability(v);
  }
}

function ability(v: EnemyView): void {
  const name = BOSS_CLIPS.ability[v.archId];
  if (name && isBossModel(v) && !v.doomed && !v.rig.busy) v.rig.playOnce(name, false, null, { solo: true });
}
